/**
 * Why each failed deploy failed.
 *
 * A failed run's record says only "Essential container in task exited". The
 * real reason is the Terraform error in its log. This store finds it:
 *
 * 1. A record the deploy pipeline already stamped with the error is used as is,
 *    with no log read. Its whole error text is read only when the row is opened.
 * 2. Otherwise the error lines are read from the log: one filtered read covers
 *    up to 100 runs, and the result is kept for the browser session.
 * 3. A run that never started, or whose log is past its 30 days, says so.
 *
 * Nothing here blocks a table: a row renders at once and its reason arrives.
 */
import {
  TASK_START_FAILURE,
  classifyDeployFailure,
  extractErrorBlocks,
  type DeployFailure,
  type DeployFailureClass,
  type DeployRetry,
} from '../../../lib/deploy-failure';
import type { DeploymentRecord } from '@/services/deploymentService';
import {
  MAX_STREAMS_PER_READ,
  isMissingStreamError,
  isThrottlingError,
  logStreamForTask,
  readErrorLines,
  resolveDeploymentLogTarget,
} from '@/services/logsService';
import { isLogExpired } from './deployStatus';

export interface FailureInfo {
  /** `loading`: the log is being read. `ready`: the verdict is in. `unavailable`: the read failed, a refresh tries again. */
  state: 'loading' | 'ready' | 'unavailable';
  failure?: DeployFailure;
  /** The whole error text, one entry for each Terraform error box. Shown in the details panel. */
  blocks: string[];
  /** Where the verdict came from. */
  source: 'record' | 'log' | 'message' | 'none';
  /** A plain sentence for the row when there is no error text to show. */
  note?: string;
  /**
   * The reason is the first attempt's. The pipeline ran the deploy a second
   * time, and the second attempt failed with no error it could read.
   */
  fromFirstAttempt?: boolean;
  /**
   * For a verdict that came with the record: the whole error text is not on
   * the record, so it is read from the log when the row is opened. Absent
   * until then, `pending` while it is read, `loaded` once `blocks` is final.
   */
  blocksState?: 'pending' | 'loaded';
}

const CACHE_PREFIX = 'deploy-failure:v2:';
const MAX_BLOCK_CHARS = 6000;
/** A big batch is led by a small one, so the top of the table fills in within a few seconds. */
const FIRST_BATCH = 10;
/**
 * Runs that started within this long of each other are read together. A fleet run's members
 * share a start time and go in one read. Measured: CloudWatch answers 70 such runs in 4 pages,
 * but 10 runs spread over two weeks in 12, so mixing far-apart runs only makes the newest wait.
 */
const BATCH_GAP_MS = 6 * 60 * 60 * 1000;
/** Reads in flight at once. Each sends about one request every two seconds: far inside the shared quota. */
const LANES = 2;
/** A run that just ended may not have its last lines searchable yet. Do not trust "no error" until then. */
const SETTLE_MS = 3 * 60 * 1000;
const RECHECK_MS = 20_000;
const MAX_RECHECKS = 4;

/** ECS's stop reason for any container exit. It says nothing, so it is never shown as the reason. */
const GENERIC_MESSAGE = /^(essential container in task exited|unknown error|exit code \d+)$/i;
const START_FAILURE_MESSAGE =
  /CannotPullContainer|TaskFailedToStart|ResourceInitializationError|CannotStartContainer|failed to start|unable to place|capacity is unavailable|Timeout waiting for network interface/i;

const cache = new Map<string, FailureInfo>();
const inFlight = new Set<string>();
const rechecks = new Map<string, number>();
const listeners = new Set<() => void>();
const lanes: Promise<void>[] = Array.from({ length: LANES }, () => Promise.resolve());
let nextLane = 0;

/** Queue one read. Reads run two at a time, in the order they were asked for. */
function enqueue(job: () => Promise<void>) {
  const lane = nextLane;
  nextLane = (nextLane + 1) % LANES;
  lanes[lane] = lanes[lane].then(job).then(emit, emit);
}

/** Cut runs (newest first) into reads: runs close in time together, at most 100 streams to a read. */
function batchesFor(records: DeploymentRecord[]): DeploymentRecord[][] {
  const clusters: DeploymentRecord[][] = [];
  let current: DeploymentRecord[] = [];
  let previous = NaN;
  for (const record of records) {
    const at = Date.parse(record.startedAt || '');
    if (current.length > 0 && !(previous - at <= BATCH_GAP_MS)) {
      clusters.push(current);
      current = [];
    }
    current.push(record);
    previous = at;
  }
  if (current.length > 0) clusters.push(current);

  const batches: DeploymentRecord[][] = [];
  for (const cluster of clusters) {
    const first = cluster.length > FIRST_BATCH * 2 ? FIRST_BATCH : 0;
    if (first) batches.push(cluster.slice(0, first));
    for (let i = first; i < cluster.length; i += MAX_STREAMS_PER_READ) {
      batches.push(cluster.slice(i, i + MAX_STREAMS_PER_READ));
    }
  }
  return batches;
}

function emit() {
  for (const listener of listeners) listener();
}

export function onFailuresChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getFailure(deploymentId: string): FailureInfo | undefined {
  return cache.get(deploymentId);
}

function persist(deploymentId: string, info: FailureInfo) {
  cache.set(deploymentId, info);
  if (info.state !== 'ready') return;
  try {
    sessionStorage.setItem(CACHE_PREFIX + deploymentId, JSON.stringify(info));
  } catch {
    // Session storage is full. The verdict stays in memory for this page.
  }
}

function restore(deploymentId: string): FailureInfo | undefined {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + deploymentId);
    if (!raw) return undefined;
    const info = JSON.parse(raw) as FailureInfo;
    return info && info.state === 'ready' ? info : undefined;
  } catch {
    return undefined;
  }
}

const RETRIES: DeployRetry[] = ['now', 'wait', 'never'];

/** The row's own words about the failure, when they say something. */
function recordText(record: DeploymentRecord): string {
  const parts = [record.message, record.error && record.details ? `${record.error}: ${record.details}` : record.error]
    .filter((part): part is string => Boolean(part && part.trim()))
    .filter((part) => !GENERIC_MESSAGE.test(part.trim()));
  return parts.join('\n');
}

/**
 * A record the pipeline stamped with its error needs no log read for its
 * reason. Whether a second run fixes it is the pipeline's own answer
 * (`errorRetry`), never worked out from the class name.
 */
function fromRecordFields(record: DeploymentRecord): FailureInfo | undefined {
  if (!record.errorSummary && !record.errorTitle && !record.errorClass) return undefined;
  // A record with no `errorRetry` (it should always have one): the shared classifier's answer for the same text.
  const verdict = classifyDeployFailure(record.errorSummary || record.errorTitle || '');
  const sameClass = !record.errorClass || record.errorClass === verdict.errorClass;
  const retry = RETRIES.includes(record.errorRetry as DeployRetry)
    ? (record.errorRetry as DeployRetry)
    : sameClass
      ? verdict.retry
      : 'never';
  const attempts = record.attemptNumber ?? 1;
  return {
    state: 'ready',
    source: 'record',
    blocks: [],
    fromFirstAttempt: (record.errorAttempt ?? attempts) < attempts,
    failure: {
      errorClass: (record.errorClass as DeployFailureClass) || verdict.errorClass,
      title: record.errorTitle || verdict.title,
      advice: record.errorAdvice || verdict.advice,
      retry,
      waitSeconds: retry !== 'never' && sameClass ? verdict.waitSeconds : 0,
      summary: record.errorSummary || verdict.summary,
    },
  };
}

const oneLine = (text: string) => text.replace(/\s+/g, ' ').trim().slice(0, 320);

/** The task existed and never ran its container: an image pull that failed, no capacity. */
function taskDidNotStart(record: DeploymentRecord): FailureInfo {
  const text = recordText(record);
  return {
    state: 'ready',
    source: 'message',
    blocks: text ? [text] : [],
    failure: { ...TASK_START_FAILURE, summary: oneLine(text) },
  };
}

/**
 * A verdict from the row's own message, for a run with no error in a log.
 * `logWasRead` separates "the log holds no error" from "there is no log".
 */
function fromRecordText(record: DeploymentRecord, opts: { logWasRead?: boolean; note?: string } = {}): FailureInfo {
  const text = recordText(record);
  if (START_FAILURE_MESSAGE.test(text)) return taskDidNotStart(record);
  if (text) {
    return { state: 'ready', source: 'message', blocks: [text], failure: classifyDeployFailure(text), note: opts.note };
  }
  if (opts.logWasRead) return { state: 'ready', source: 'none', blocks: [], failure: classifyDeployFailure([]) };
  return {
    state: 'ready',
    source: 'none',
    blocks: [],
    note: opts.note || 'This run recorded no reason and wrote no log.',
  };
}

/** What can be said about a failed run with no log read, or undefined when the log must be read. */
function immediate(record: DeploymentRecord): FailureInfo | undefined {
  const stamped = fromRecordFields(record);
  if (stamped) return stamped;
  const { stream } = resolveDeploymentLogTarget(record);
  // No task means no log: the run was refused or never launched.
  if (!stream) return fromRecordText(record);
  if (START_FAILURE_MESSAGE.test(recordText(record))) return fromRecordText(record);
  if (isLogExpired(record)) return fromRecordText(record, { note: 'The log for this run has expired.' });
  return undefined;
}

function fromLogLines(record: DeploymentRecord, lines: string[] | undefined): FailureInfo {
  const blocks = extractErrorBlocks(lines || []);
  if (blocks.length === 0) {
    // The log has no error. The row's own message is the best there is (a caught pipeline error, a manual stop).
    return fromRecordText(record, { logWasRead: true });
  }
  return {
    state: 'ready',
    source: 'log',
    blocks: blocksToShow(blocks),
    // Classified from every block, the same input the deploy pipeline uses.
    failure: classifyDeployFailure(blocks),
  };
}

/**
 * The error blocks worth reading. cdktf repeats a failure after Terraform
 * prints it ("Error: non-zero exit code 1", or the same error a second time),
 * and those echoes would make one error look like three.
 */
function blocksToShow(blocks: string[]): string[] {
  const seen = new Set<string>();
  const real = blocks.filter((block) => {
    const text = block.trim();
    if (/^Error:\s*non-zero exit code \d+$/i.test(text) || seen.has(text)) return false;
    seen.add(text);
    return true;
  });
  return (real.length > 0 ? real : blocks.slice(0, 1)).map((block) => block.slice(0, MAX_BLOCK_CHARS));
}

function windowFor(records: DeploymentRecord[]): { startTime: number; endTime: number } {
  let startTime = Infinity;
  let endTime = 0;
  for (const record of records) {
    const started = Date.parse(record.startedAt || '');
    if (!Number.isFinite(started)) continue;
    const ended = Date.parse(record.endedAt || '');
    startTime = Math.min(startTime, started);
    // A deploy stops itself at two hours, so a row with no end time ended within three.
    endTime = Math.max(endTime, Number.isFinite(ended) ? ended : started + 3 * 60 * 60 * 1000);
  }
  if (!Number.isFinite(startTime)) return { startTime: 0, endTime: Date.now() };
  return { startTime: startTime - 60_000, endTime: endTime + 5 * 60_000 };
}

/**
 * Read one batch. CloudWatch refuses the whole read when any one stream does
 * not exist (a task that never wrote a line), so a refused batch is cut in two
 * and each half read again, down to the one run that has no log.
 */
async function readBatch(group: string, records: DeploymentRecord[]): Promise<void> {
  const streamOf = (record: DeploymentRecord) => resolveDeploymentLogTarget(record).stream!;
  try {
    const byStream = await readErrorLines({ group, streams: records.map(streamOf), ...windowFor(records) });
    for (const record of records) settle(record, fromLogLines(record, byStream.get(streamOf(record))));
  } catch (error) {
    if (isMissingStreamError(error)) {
      if (records.length === 1) {
        // A task with no log stream never ran its container.
        settle(records[0], taskDidNotStart(records[0]));
        return;
      }
      const middle = Math.ceil(records.length / 2);
      await readBatch(group, records.slice(0, middle));
      await readBatch(group, records.slice(middle));
      return;
    }
    throw error;
  }
}

function settle(record: DeploymentRecord, info: FailureInfo) {
  inFlight.delete(record.deploymentId);
  // The record was stamped with its error while the log read was in flight. Keep the record's word.
  if (cache.get(record.deploymentId)?.source === 'record') return;
  const ended = Date.parse(record.endedAt || '');
  const justEnded = Number.isFinite(ended) && Date.now() - ended < SETTLE_MS;
  const tries = rechecks.get(record.deploymentId) || 0;
  if (info.source !== 'log' && justEnded && tries < MAX_RECHECKS) {
    // The run ended moments ago and its last lines may not be searchable yet. Look again shortly.
    rechecks.set(record.deploymentId, tries + 1);
    cache.set(record.deploymentId, { state: 'loading', source: 'none', blocks: [] });
    setTimeout(() => {
      cache.delete(record.deploymentId);
      requestFailures([record]);
    }, RECHECK_MS);
    return;
  }
  persist(record.deploymentId, info);
}

async function runBatch(group: string, records: DeploymentRecord[]): Promise<void> {
  const delays = [2_000, 6_000, 15_000];
  for (let attempt = 0; ; attempt++) {
    try {
      await readBatch(group, records);
      return;
    } catch (error) {
      if (isThrottlingError(error) && attempt < delays.length) {
        await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
        continue;
      }
      console.warn('Could not read why these deploys failed', error);
      for (const record of records) {
        if (!inFlight.has(record.deploymentId)) continue;
        inFlight.delete(record.deploymentId);
        cache.set(record.deploymentId, {
          state: 'unavailable',
          source: 'none',
          blocks: [],
          note: 'Could not read the log for this run. Refresh to try again.',
        });
      }
      return;
    }
  }
}

/**
 * Make sure a verdict exists, or is on its way, for each failed run given.
 * Safe to call on every render: a run already known or being read is skipped.
 */
export function requestFailures(records: DeploymentRecord[]): void {
  const pending: DeploymentRecord[] = [];
  let changed = false;
  for (const record of records) {
    const id = record.deploymentId;
    const known = cache.get(id);
    if (known) {
      // The pipeline can stamp the error on the record a moment after the run ends. Its word wins over a log read.
      const stamped = known.source === 'record' ? undefined : fromRecordFields(record);
      if (stamped) {
        inFlight.delete(id);
        cache.set(id, stamped);
        changed = true;
      }
      continue;
    }
    const saved = restore(id);
    const instant = immediate(record) || saved;
    if (instant) {
      // The whole error text of a stamped record, read earlier in this session, is kept with it.
      const kept = instant.source === 'record' && saved?.blocksState === 'loaded';
      cache.set(id, kept ? { ...instant, blocks: saved.blocks, blocksState: 'loaded' } : instant);
      changed = true;
      continue;
    }
    inFlight.add(id);
    cache.set(id, { state: 'loading', source: 'none', blocks: [] });
    pending.push(record);
    changed = true;
  }
  if (changed) emit();
  if (pending.length === 0) return;

  // Newest first, so the rows at the top of the table get their reason first.
  pending.sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));
  const byGroup = new Map<string, DeploymentRecord[]>();
  for (const record of pending) {
    const { group } = resolveDeploymentLogTarget(record);
    byGroup.set(group, [...(byGroup.get(group) || []), record]);
  }
  for (const [group, groupRecords] of byGroup) {
    for (const batch of batchesFor(groupRecords)) enqueue(() => runBatch(group, batch));
  }
}

/** Read the whole error text of one run whose reason came with its record. */
async function readDetail(record: DeploymentRecord, group: string, stream: string): Promise<void> {
  const id = record.deploymentId;
  let blocks: string[] = [];
  try {
    const byStream = await readErrorLines({ group, streams: [stream], ...windowFor([record]) });
    blocks = blocksToShow(extractErrorBlocks(byStream.get(stream) || []));
  } catch (error) {
    if (!isMissingStreamError(error)) {
      console.warn('Could not read the full error for this deploy', error);
      // Leave it unread, so opening the row again tries once more.
      const current = cache.get(id);
      if (current?.source === 'record') cache.set(id, { ...current, blocksState: undefined });
      return;
    }
  }
  const current = cache.get(id);
  if (current?.source === 'record') persist(id, { ...current, blocks, blocksState: 'loaded' });
}

/**
 * Read the whole error text for runs whose reason came with the record. Call
 * it for an opened row only: the row's one-line reason never needs it.
 */
export function requestFailureDetails(records: DeploymentRecord[]): void {
  let changed = false;
  for (const record of records) {
    const id = record.deploymentId;
    const info = cache.get(id);
    if (!info || info.source !== 'record' || info.blocksState) continue;
    const target = resolveDeploymentLogTarget(record);
    // A reason from the first attempt is in the first attempt's log, not the current one.
    const stream =
      info.fromFirstAttempt && record.firstAttemptTaskArn
        ? logStreamForTask(record.firstAttemptTaskArn)
        : target.stream;
    changed = true;
    if (!stream || isLogExpired(record)) {
      cache.set(id, {
        ...info,
        blocksState: 'loaded',
        note: stream ? 'The full error text went with the log, which is kept for 30 days.' : info.note,
      });
      continue;
    }
    cache.set(id, { ...info, blocksState: 'pending' });
    enqueue(() => readDetail(record, target.group, stream));
  }
  if (changed) emit();
}

/** Forget failed reads, so the next request tries them again. Called by a manual refresh. */
export function retryUnavailableFailures(): void {
  for (const [id, info] of cache) {
    if (info.state === 'unavailable') cache.delete(id);
  }
}
